import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { array, mixed } from 'yup';
import sortFilterLimitedOptions from '../../../basics/options/sotrFilterLimited.options';
import DateTimeDisplay from '../../../yoda-ui/components/DateTimeDisplay/DateTimeDisplay';
import { VeevaSyncStatusBadgeMap } from 'basics/constants/common.constant';
import { CepStatus, CepStatusActions, VeevaSyncStatus } from 'basics/enums/maya.enums';
import biogenFunctionOptions from 'basics/options/biogenFunction.options';
import useCepStatusOptions from 'basics/options/cepStatus.options';
import useCustomerInitiativeTypeOptions from 'basics/options/customerInitiative.options';
import useProductOptions from 'basics/options/product.options';
import useTherapeuticAreaOptions from 'basics/options/therapeuticArea.options';
import GridListAction from 'components/GridListAction/GridListAction';
import { ProductsContext } from 'contexts/products';
import { SortFilter } from 'generated/maya.types';
import Tag from 'yoda-ui/components/Tags/Tag';
import { YodaBorderRadius, YodaColors, YodaFontSize, YodaFontWeight, YodaSpacing } from 'yoda-ui/yodaTheme';

const useCepListConfig = (
  setCepToFeedbackCallback: (cepId: string) => void,
  setCepToApprovedCallback: (cepId: string) => void,
  syncCepCallback: (cepId: string) => void,
  editCepCallback: (cepId: string) => void,
  duplicateCepCallback: (cepId: string) => void,
  openModalCallback: (cepId: string, action: CepStatusActions) => void,
  therapeuticArea: string[],
) => {
  const { t } = useTranslation();
  const flags = useFlags();
  const { allProducts } = useContext(ProductsContext);
  const currentTA = therapeuticArea ?? [];

  const CepStatusBadgeMap = {
    [CepStatus.DRAFT as string]: {
      label: t('maya_status_draft'),
      bgColor: YodaColors.errorPurple,
      color: YodaColors.errorPurpleLight,
    },
    [CepStatus.APPROVED as string]: {
      label: t('maya_status_approved'),
      bgColor: YodaColors.greenSuccessLight,
      color: YodaColors.greenSuccessDark,
    },
    [CepStatus.FEEDBACK as string]: {
      label: t('maya_status_feedback'),
      bgColor: YodaColors.warningLight,
      color: YodaColors.warning,
    },
    [CepStatus.CANCELLED as string]: {
      label: 'Cancelled',
      bgColor: YodaColors.redErrorLight,
      color: YodaColors.redErrorDark,
    },
    [CepStatus.ONGOING as string]: {
      label: t('maya_status_ongoing'),
      bgColor: YodaColors.warningLight,
      color: YodaColors.warning,
    },
  };

  const cepTitleFieldConfig = {
    name: 'name',
    placeholder: t('maya_cep_form_cep_name_placeholder'),
    defaultValue: '',
    required: false,
  };

  const statusFieldConfig = {
    name: 'status',
    placeholder: t('status_placeholder'),
    options: useCepStatusOptions(),
    defaultValue: [],
    required: false,
    validation: array()
      .of(mixed<CepStatus>().oneOf(Object.values(CepStatus))),
    disableRenderTags: true,
  };

  const customerInitiativeFieldConfig = {
    name: 'initiative',
    placeholder: t('maya_cep_form_ci_placeholder'),
    options: useCustomerInitiativeTypeOptions({ isTemplate: false }),
    defaultValue: [],
    required: false,
    validation: array(),
    disableRenderTags: true,
  };

  const cepTherapeuticAreaFieldConfig = {
    name: 'therapeuticArea',
    placeholder: t('maya_cep_form_cep_therapeutic_area_placeholder'),
    options: useTherapeuticAreaOptions(allProducts),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const cepProductFieldConfig = {
    name: 'product',
    placeholder: t('maya_cep_form_cep_product_placeholder'),
    options: useProductOptions(currentTA, allProducts),
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const cepBiogenFunctionFieldConfig = {
    name: 'cepBiogenFunction',
    placeholder: t('maya_cep_form_cep_function_placeholder'),
    options: biogenFunctionOptions,
    defaultValue: [],
    required: false,
    disableRenderTags: true,
  };

  const cepSortFieldConfig = {
    name: 'sort',
    placeholder: t('sort_placeholder'),
    options: sortFilterLimitedOptions,
    defaultValue: SortFilter.NEWEST,
    required: false,
    disableRenderTags: true,
  };

  const cepListColumnConfig: GridColDef[] = [
    {
      field: 'title',
      headerName: t('maya_cep_list_title_header_label'),
      flex: 0.5,
      sortable: false,
      cellClassName: 'gridlist-cell-bold',
    },
    {
      field: 'formattedCepId',
      headerName: t('maya_cep_list_cep_id_header_label'),
      flex: 0.1,
      sortable: false,
    },
    {
      field: 'published',
      type: 'date',
      headerName: t('maya_cep_list_published_date_header_label'),
      renderCell: (cellValues: GridRenderCellParams) => {
        if (!cellValues.value) return null;

        const publishedDate = cellValues.value instanceof Date ? cellValues.value : new Date(cellValues.value as string);

        return (
          cellValues.value
            ? <DateTimeDisplay
              date={ publishedDate.toLocaleDateString('en-GB') }
              time={ publishedDate.toLocaleTimeString('uk-UA', { hour12: false }) }
            /> : ''
        );
      },
      flex: 0.2,
      sortable: false,
    },
    {
      field: 'globalCepSyncStatus',
      headerName: t('maya_veeva_sync_status_header_label'),
      flex: 0.1,
      renderCell: (cellValues: GridRenderCellParams) => {
        const badge = VeevaSyncStatusBadgeMap[cellValues.row.globalCepSyncStatus.syncStatus || VeevaSyncStatus.PENDING];
        return badge && (
          <Tag
            fitContent
            bgcolor={ badge.bgColor }
            color={ badge.color }
            fontWeight={ YodaFontWeight.medium }
            fontSize={ YodaFontSize.small }
            paddingY={ YodaSpacing.xxxxSmall }
            paddingX={ YodaSpacing.xSmall }
            width={ 'auto' }
            height={ '20px' }
            borderRadius={ YodaBorderRadius.xSmall }
          >
            { badge.label }
          </Tag>
        );
      },
      sortable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.1,
      renderCell: (cellValues: GridRenderCellParams) => {
        const badge = CepStatusBadgeMap[cellValues.row.status];
        return badge && (
          <Tag
            fitContent
            bgcolor={ badge.bgColor }
            color={ badge.color }
            fontWeight={ YodaFontWeight.medium }
            fontSize={ YodaFontSize.small }
            paddingY={ YodaSpacing.xxxxSmall }
            paddingX={ YodaSpacing.xSmall }
            width={ 'auto' }
            height={ '20px' }
            borderRadius={ YodaBorderRadius.xSmall }
          >
            { badge.label }
          </Tag>
        );
      },
      sortable: false,
    },
    {
      field: 'action',
      headerName: '',
      disableColumnMenu: true,
      hideSortIcons: true,
      renderCell: (params: GridRenderCellParams) => {
        const cep = params.row;
        const actions = [];

        if (cep.status !== CepStatus.CANCELLED) {
          actions.push({
            label: t('actions_edit_label'),
            callback: () => { editCepCallback(cep._id); },
          });

          actions.push({
            label: t('actions_duplicate_label'),
            callback: () => { duplicateCepCallback(cep._id); },
          });
        }

        if (cep.status === CepStatus.DRAFT) {
          actions.push({
            label: t('maya_action_feedback_cep_label'),
            callback: () => {
              setCepToFeedbackCallback(cep._id);
            },
          });
        }

        if (cep.status === CepStatus.FEEDBACK) {
          actions.push({
            label: t('maya_action_approve_cep_label'),
            callback: () => {
              setCepToApprovedCallback(cep._id);
            },
          });
        }

        if (
          flags.mayaBiogenlinc3595EnableCepDeleting
          && (!flags.mayaBdcfcsd653ExtendEditingOfApprovedCep || !flags.mayaBdcfcsd1251EnableSettingCancelledStatusForCep)
          && cep.status !== CepStatus.APPROVED
        ) {
          actions.push({
            label: t('maya_action_delete_cep_label'),
            callback: () => {
              openModalCallback(cep._id, CepStatusActions.DELETE);
            },
          });
        }

        if (
          flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
          && flags.mayaBdcfcsd1251EnableSettingCancelledStatusForCep
          && cep.status !== CepStatus.APPROVED
          && cep.status !== CepStatus.CANCELLED
        ) {
          actions.push({
            label: t('maya_action_cancel_cep_label'),
            callback: () => {
              openModalCallback(cep._id, CepStatusActions.CANCEL);
            },
          });
        }

        if (
          flags.mayaBdcfcsd653ExtendEditingOfApprovedCep
          && flags.mayaBdcfcsd1251EnableSettingCancelledStatusForCep
          && cep.status === CepStatus.CANCELLED
        ) {
          actions.push({
            label: t('maya_action_restore_cep_label'),
            callback: () => {
              openModalCallback(cep._id, CepStatusActions.RESTORE);
            },
          });
        }

        if (flags.mayaBiogenlinc2489VeevaSyncButtons) {
          actions.push({
            label: t('maya_action_sync_cep_label'),
            callback: () => {
              syncCepCallback(cep._id);
            },
          });
        }

        return (
          <GridListAction
            actions={ actions }
          />
        );
      },
      align: 'right',
      headerAlign: 'right',
      flex: 0.01,
    },
  ];

  return {
    cepListColumnConfig,
    cepTitleFieldConfig,
    statusFieldConfig,
    customerInitiativeFieldConfig,
    cepTherapeuticAreaFieldConfig,
    cepProductFieldConfig,
    cepBiogenFunctionFieldConfig,
    cepSortFieldConfig,
  };
};

export default useCepListConfig;
